

import React, { useEffect, useState } from "react";
import "./Analysis.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import axios from "axios";

const StandardReport = () => {
  const [vendorId, setVendorId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [recommendations, setRecommendations] = useState([]);

  const isMobile = window.innerWidth <= 768;
  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#A28BFE"];

  useEffect(() => {
    const storedVendorId = localStorage.getItem("vendorId");
    if (storedVendorId) setVendorId(storedVendorId);
  }, []);
  const containerHeight = window.innerWidth < 768
    ? '400px'       // Mobile
    : window.innerWidth < 850
      ? '450px'     // Below 850px
      : window.innerWidth < 1400
        ? '410px'   // Between 850px and 1399px
        : window.innerWidth < 1600
          ? '540px' // Between 1400px and 1599px
          : '700px'; // Above 1600px


  useEffect(() => {
    if (!vendorId) return;

    const fetchData = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;

        const res = await axios.get(
          `https://backend.maamaas.com/food/api/orders/vendor/statistics/${vendorId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setData(res.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching report:", err);
        setLoading(false);
      }
    };

    fetchData();
  }, [vendorId]);



  useEffect(() => {
    if (!vendorId) return;

    const fetchOrderData = async () => {
      try {
        const response = await axios.get(
          `https://backend.maamaas.com/food/api/orders/vendor/statistics/${vendorId}`
        );
        const orders = response.data;

        generateRecommendations(orders);
      } catch (error) {
        console.error("Error fetching order data:", error);
      }
    };

    fetchOrderData();
  }, [vendorId]);


  const generateRecommendations = (orders) => {
    const currentRecommendations = [];

    // 📊 1. Peak Sales Between 7-9 PM
    const eveningOrders = orders.filter(order => {
      const orderHour = new Date(order.createdAt).getHours();
      return orderHour >= 19 && orderHour <= 21;
    });
    const eveningRatio = (eveningOrders.length / orders.length) * 100;
    if (eveningRatio > 30) {
      currentRecommendations.push(
        '"Peak sales happen between 7-9PM. Consider discounts!"'
      );
    }

    // 💳 2. Payment Preferences
    const onlinePayments = orders.filter(order => order.paymentMode === "Online");
    const onlinePaymentRatio = (onlinePayments.length / orders.length) * 100;
    if (onlinePaymentRatio > 70) {
      currentRecommendations.push(
        '"80% of Customers prefer online payments. Reduce COD options!"'
      );
    }

    // ❗ 3. Refund Rate
    const refundOrders = orders.filter(order => order.status === "Refunded");
    const refundRate = (refundOrders.length / orders.length) * 100;
    if (refundRate > 5) {
      currentRecommendations.push(
        '"Refund Rate increased by 5%. Analyze refund reasons!"'
      );
    }

    setRecommendations(currentRecommendations);
  };

  if (loading) {
    return (
      <div
        style={{
          height: "500px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "24px",
          fontWeight: "bold",
        }}
      >
        Loading...
      </div>
    );
  }

  if (!data) {
    return <div>No Data Found</div>;
  }

  // ✅ Transform API response for charts
  const paymentData = data.paymentDistribution.map((p) => ({
    name: p.method,
    value: p.count,
    percent: parseFloat(p.percentage),
  }));

  const topItems = data.topSellingItems.map((item) => {
    const key = Object.keys(item)[0];
    return { name: key, orders: item[key] };
  });

  const monthlyData = [
    {
      month: "This Week",
      Orders: data.weeklyOrders,
      Revenue: data.weeklyRevenue,
    },
    {
      month: "Today",
      Orders: data.dailyOrders,
      Revenue: data.dailyRevenue,
    },
  ];





  return (
    <div
      className="cards"
      style={{
        overflowY: "auto",
        height: containerHeight,
        marginTop: "30px",
        scrollbarWidth: "none",
      }}
    >
      {/* Status Cards */}
      <div className="status-overview4">
        <div style={{ background: "gray", color: "#fff" }} className="status-card4">
          <h3>Total Orders</h3>
          <p>{data.totalOrders}</p>
        </div>
        <div style={{ background: "gray", color: "#fff" }} className="status-card4">
          <h3>Total Revenue</h3>
          <p>₹{data.totalRevenue}</p>
        </div>
        <div style={{ background: "gray", color: "#fff" }} className="status-card4">
          <h3>Total Customers</h3>
          <p>{data.totalCustomers}</p>
        </div>
        <div style={{ background: "gray", color: "#fff" }} className="status-card4">
          <h3>Success Rate</h3>
          <p>{data.successRate}</p>
        </div>
      </div>

      {/* Charts */}
      <div
        className="cards"
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: "16px",
          marginTop: "20px",
        }}
      >
        {/* Orders & Revenue */}
        <div style={{ padding: "16px", flex: "1 1 48%", minWidth: "300px" }}>
          <h2
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              color: "red",
              marginBottom: "16px",
            }}
          >
            Orders & Revenue
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="Orders" fill="#1f77b4" />
              <Bar dataKey="Revenue" fill="#d62728" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top Selling Items */}
        <div style={{ padding: "16px", flex: "1 1 48%", minWidth: "300px" }}>
          <h2
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              color: "red",
              marginBottom: "16px",
            }}
          >
            Top Selling Items
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topItems}>
              <CartesianGrid strokeDasharray="2 2" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="orders" fill="#1f77b4" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Payment Distribution */}
      <div style={{ padding: "16px", textAlign: "center" }}>
        <h2
          style={{
            fontSize: "20px",
            fontWeight: "bold",
            color: "red",
            marginBottom: "16px",
          }}
        >
          Payment Distribution
        </h2>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={paymentData}
              cx="50%"
              cy="50%"
              outerRadius={100}
              dataKey="value"
              label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
            >
              {paymentData.map((entry, index) => (
                <Cell key={index} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div style={{ marginLeft: "50px" }}>
        <h3 style={{ color: "red" }}>Recommendations</h3>
        {recommendations.length > 0 ? (
          recommendations.map((msg, index) => (
            <p key={index} style={{ fontWeight: "bold" }}>
              {msg}
            </p>
          ))
        ) : (
          <p style={{ fontWeight: "bold" }}>No specific recommendations for now.</p>
        )}
      </div>
      <style>
        {`.status-overview4 {
    display: flex;
    justify-content: space-evenly;
    margin-bottom: 10px;
    gap: 10px;
  }
  .status-card4 {
    padding: 8px;
    border-radius: 8px;
    width: 28%;
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    color: #000;
    height: 100px;
    width: 180px;
  }
  /* Base styling (you already have this inline, can be moved to class) */



/* Media Query for tablets (up to 768px) */
/* Default Styles (Desktop and larger) */

/* Tablet and below: 768px */
@media (max-width: 768px) {
  .status-overview4 {
    flex-direction: column;
    /* align-items: center; */
  }

  .status-card4 {
    width: 98%;
    max-width: 100%;
    margin-bottom: 15px;
  }

  .tab-buttons-container {
    flex-wrap: wrap;
    justify-content: center;
  }

  .tab-buttons-container button {
    width: 45%;
    margin: 5px;
  }

  .responsive-container {
    width: 100% !important;
    height: 250px !important;
  }

  h2 {
    font-size: 18px !important;
    text-align: center;
  }

  .pie-chart-container {
    width: 100%;
    margin: 0 auto;
  }

  .recommendations {
    margin-left: 20px !important;
  }

  .chart-box {
    flex: 1 1 100%;
    max-width: 100%;
  }
}

/* Small devices: up to 480px (phones) */
@media (max-width: 480px) {
  .status-card4 {
    width: 95%;
    margin-bottom: 10px;
  }

  .tab-buttons-container button {
    width: 100%;
    margin: 5px 0;
  }

  .responsive-container {
    width: 100% !important;
    height: 200px !important;
  }

  .pie-chart-container {
    width: 100%;
    padding: 0 10px;
  }

  .recommendations {
    margin-left: 10px !important;
    padding-right: 10px;
  }

  h2 {
    font-size: 16px !important;
  }
}

/* Extra small screens (320px and below) */
@media (max-width: 360px) {
  .tab-buttons-container button {
    font-size: 14px;
    padding: 8px;
  }

  .responsive-container {
    height: 180px !important;
  }

  h2 {
    font-size: 14px !important;
  }

  .recommendations {
    font-size: 14px;
  }
}
`}
      </style>
    </div>




  );
};

export default StandardReport;

